import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { collection, onSnapshot, doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './ShortlistCreatorModal.css';

function ShortlistCreatorModal({ isOpen, onClose, creator }) {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch user's campaigns
  useEffect(() => {
    if (!isOpen) return;

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
    const unsubscribe = onSnapshot(campaignsRef, (snapshot) => {
      const fetchedCampaigns = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(c => !c.archived); // Only show active campaigns
      
      setCampaigns(fetchedCampaigns);
      
      // Set first campaign as default if available
      if (fetchedCampaigns.length > 0 && !selectedCampaign) {
        setSelectedCampaign(fetchedCampaigns[0].id);
      }
    });

    return () => unsubscribe();
  }, [isOpen]);

  const handleShortlist = async () => {
    if (!selectedCampaign || !creator) return;

    setLoading(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      const campaignRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign);
      const campaignDoc = await getDoc(campaignRef);

      if (campaignDoc.exists()) {
        const campaignData = campaignDoc.data();
        const existingCreators = campaignData.creators || [];

        // Check if creator is already shortlisted
        const isAlreadyShortlisted = existingCreators.some(c => c.id === creator.id);

        if (isAlreadyShortlisted) {
          alert('This creator is already shortlisted for this campaign!');
          setLoading(false);
          return;
        }

        // Store only essential creator info (full portfolio will be loaded from Find Creators panel)
        const creatorData = {
          id: creator.id,
          name: creator.name || 'Unknown Creator',
          handle: creator.handle || '@unknown',
          avatar: creator.image || creator.profileImage || creator.avatar || '/creatorimages/creatorpic1v2-min.png',
          profileImage: creator.image || creator.profileImage || creator.avatar || '/creatorimages/creatorpic1v2-min.png',
          niche: creator.niche || 'General',
          tier: creator.tier || 'Rising Star',
          followers: creator.followers || 0,
          avgViews: creator.avgViews || 0,
          shortlistedAt: new Date().toISOString()
        };

        await updateDoc(campaignRef, {
          creators: arrayUnion(creatorData)
        });

        alert('Creator shortlisted successfully!');
        onClose();
      }
    } catch (error) {
      console.error('Error shortlisting creator:', error);
      alert('Failed to shortlist creator. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="shortlist-modal-overlay" onClick={onClose}>
      <div className="shortlist-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="shortlist-modal-header">
          <h3>Shortlist Creator</h3>
          <button className="shortlist-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="shortlist-modal-body">
          {/* Creator Preview */}
          <div className="shortlist-creator-preview">
            <img 
              src={creator?.image || creator?.profileImage || creator?.avatar || '/creatorimages/creatorpic1v2-min.png'} 
              alt={creator?.name}
              className="shortlist-creator-avatar"
            />
            <div className="shortlist-creator-info">
              <div className="shortlist-creator-name">{creator?.name}</div>
              <div className="shortlist-creator-handle">{creator?.handle}</div>
            </div>
          </div>

          {/* Campaign Selection */}
          <div className="shortlist-form-group">
            <label htmlFor="campaign-select">Select Campaign</label>
            {campaigns.length === 0 ? (
              <p className="shortlist-no-campaigns">
                No active campaigns found. Create a campaign first to shortlist creators.
              </p>
            ) : (
              <select
                id="campaign-select"
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
                className="shortlist-campaign-select"
              >
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="shortlist-modal-footer">
          <button 
            className="shortlist-btn-cancel" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="shortlist-btn-confirm" 
            onClick={handleShortlist}
            disabled={loading || campaigns.length === 0}
          >
            {loading ? 'Shortlisting...' : 'Add to Shortlist'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShortlistCreatorModal;

